import { Button, Icon, Modal } from '@aidkitorg/component-library';
import React, { useCallback, useState } from 'react';
import { useApplicantsContext } from '../../../Contexts/ApplicantsContext';
import * as XLSX from 'xlsx';
import { useLocalizedStrings } from '../../../Localization';
import { getTodayAsYYYYMMDD } from '../../../utils/adminPanel.utils';

type ApplicantExportModalProps = {
  exportModalOpen: boolean;
  setExportModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

export const ApplicantExportModal: React.FC<ApplicantExportModalProps> = ({
  exportModalOpen,
  setExportModalOpen,
}) => {
  const L = useLocalizedStrings();
  const { queryParams, fetchApplicantsForExport, logApplicantExport } = useApplicantsContext();
  const [isExporting, setIsExporting] = useState(false);

  const processApplicantData = (applicants: any[]) => {
    return applicants.map(applicant => {
      const processedApplicant: any = {
        uid: applicant.uid,
        legal_name: applicant.legal_name || '',
        status: applicant.status || 'pending',
        organization: applicant.organization || '',
        source: applicant.source || '',
      };

      // Add created_at if it exists
      if (applicant.created_at) {
        const date = new Date(applicant.created_at * 1000); // Assuming Unix timestamp
        processedApplicant.created_at = String(new Intl.DateTimeFormat("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit"
        }).format(date));
      }

      // Include any additional fields from applicantinfo
      Object.keys(applicant).forEach(key => {
        if (!['uid', 'legal_name', 'status'].includes(key)) {
          processedApplicant[key] = applicant[key];
        }
      });

      return processedApplicant;
    });
  };

  const downloadApplicantsAsCSV = useCallback(async () => {
    setIsExporting(true);
    try {
      const applicants = await fetchApplicantsForExport();
      const processedApplicants = processApplicantData(applicants);

      const convertToCSV = (objArray: any[]): string => {
        if (objArray.length === 0) return '';
        const array = [Object.keys(objArray[0])].concat(objArray.map(obj => Object.values(obj)));
        return array
          .map(row =>
            row.map(String).map(val => `"${val.replace(/"/g, '""')}"`).join(',')
          )
          .join('\n');
      };

      const csv = convertToCSV(processedApplicants);
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);

      const fileName = `Applicants_${getTodayAsYYYYMMDD()}.csv`;

      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      await logApplicantExport('CSV', processedApplicants.length);
      setExportModalOpen(false);
    } catch (error) {
      console.error('Failed to export applicants as CSV:', error);
      alert('Failed to export applicants. Please try again.');
    } finally {
      setIsExporting(false);
    }
  }, [fetchApplicantsForExport, logApplicantExport, setExportModalOpen]);

  const downloadApplicantsAsXLSX = useCallback(async () => {
    setIsExporting(true);
    try {
      const applicants = await fetchApplicantsForExport();
      const processedApplicants = processApplicantData(applicants);

      const worksheet = XLSX.utils.json_to_sheet(processedApplicants);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Applicants');

      const fileName = `Applicants_${getTodayAsYYYYMMDD()}.xlsx`;

      XLSX.writeFile(workbook, fileName);

      await logApplicantExport('XLSX', processedApplicants.length);
      setExportModalOpen(false);
    } catch (error) {
      console.error('Failed to export applicants as XLSX:', error);
      alert('Failed to export applicants. Please try again.');
    } finally {
      setIsExporting(false);
    }
  }, [fetchApplicantsForExport, logApplicantExport, setExportModalOpen]);

  return (
    <Modal
      title={L.admin_panel.applicants.all_applicants.export.title}
      open={exportModalOpen}
      onClose={() => setExportModalOpen(false)}
    >
      <div className="pb-4">
        {L.admin_panel.applicants.all_applicants.export.description}
        {queryParams.search ? L.admin_panel.applicants.all_applicants.export.description_with_filter : ''}.
      </div>
      <div className="flex flex-col gap-y-2">
        <Button
          onClick={downloadApplicantsAsCSV}
          variant="secondary"
          className="!justify-start"
          disabled={isExporting}
        >
          <div className="flex gap-4 items-center py-2">
            <Icon name="ArrowDownTrayIcon" role='decorative'/>
            <div className="!text-left">
              <div>{L.admin_panel.applicants.all_applicants.export.csv_format}</div>
              <div className="font-light">{L.admin_panel.applicants.all_applicants.export.csv_format_desc}</div>
            </div>
          </div>
        </Button>
        <Button
          onClick={downloadApplicantsAsXLSX}
          variant="secondary"
          className="!justify-start"
          disabled={isExporting}
        >
          <div className="flex gap-4 items-center py-2">
            <Icon name="TableCellsIcon" role='decorative'/>
            <div className="!text-left">
              <div>{L.admin_panel.applicants.all_applicants.export.xlsx_format}</div>
              <div className="font-light">{L.admin_panel.applicants.all_applicants.export.xlsx_format_desc}</div>
            </div>
          </div>
        </Button>
      </div>
      {isExporting && (
        <div className="mt-4 text-sm text-gray-600 text-center">
          {L.admin_panel.applicants.all_applicants.export.exporting}
        </div>
      )}
    </Modal>
  );
};
